import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAIFormCheckVisibility } from '../hooks/useAIFormCheckVisibility';
import {
  Compass,
  ChevronDown,
  Home,
  Sparkles,
  Users,
  Laptop,
  UserCheck,
  MapPin,
  Package,
  Trophy,
  Phone,
  ScanLine,
  GitCompareArrows,
  Layers,
  Award,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

/**
 * ExploreLGRAMenu — desktop-only compact dropdown ("Explore LGRA") that sits
 * beside the logo in `PublicHeader`. Gives guests and signed-in students
 * one-click access to every homepage section (via stable hash anchors) plus
 * the two off-homepage links that belong in the "explore" mental model
 * (Student Stories · Contact LGRA).
 *
 * Behaviour
 *   • Clicking the LGRA logo itself keeps its existing "Return to Homepage"
 *     function — this menu is a sibling, not a replacement.
 *   • Same-page navigation smooth-scrolls to the anchor without triggering
 *     a full router transition (avoids remounting the homepage tree).
 *   • Cross-page navigation routes to `/#anchor`; `PublicLanding.js` already
 *     re-anchors on hash change so the section lands correctly.
 *   • Escape / outside-click / route change all close the menu (Radix
 *     DropdownMenu takes care of Escape + outside-click natively).
 *   • Renders only from `lg:` upward — on <lg the identical grouped menu is
 *     inside `MobileNavDrawer` so tablets & phones never see two menus.
 *
 * The `#contact` link scrolls to the site-wide `PublicFooter` (which now
 * carries `id="contact"`). The `#top` link scrolls to the very top of the
 * homepage (`<span id="top">` at the head of `PublicLanding.js`).
 */

const START_HERE = [
  { label: 'Home', to: '/#top', testId: 'explore-home', Icon: Home },
  { label: 'Find My Best Training Plan', to: '/online-training-plan', testId: 'explore-find-my-class', Icon: Sparkles },
  { label: 'Choose Your Training Path', to: '/#training-options', testId: 'explore-training-options', Icon: Compass },
  { label: 'Compare Training Options', to: '/compare-training-options', testId: 'explore-compare-training', Icon: GitCompareArrows },
];

const TRAINING_OPTIONS = [
  { label: 'Running Classes', to: '/running-classes', testId: 'explore-physical-classes', Icon: Users },
  { label: 'Online Training Plan', to: '/online-training-plan', testId: 'explore-online-training-plan', Icon: Laptop },
  { label: 'Private Coaching', to: '/coaching/private', testId: 'explore-private-coaching', Icon: UserCheck },
  { label: 'Running Technique', to: '/coaching/running-technique', testId: 'explore-technique', Icon: Award },
  { label: 'AI Running Form Check', to: '/ai-running-form-check', testId: 'explore-ai-form-check', Icon: ScanLine, badge: 'NEW' },
];

const EXPLORE_MORE = [
  { label: 'All Classes', to: '/all-classes', testId: 'explore-all-classes', Icon: Layers },
  { label: 'Workshops & Events', to: '/workshops', testId: 'explore-workshops', Icon: Trophy },
  { label: 'Training Locations', to: '/#locations', testId: 'explore-locations', Icon: MapPin },
  { label: 'Membership & Packages', to: '/packages', testId: 'explore-packages', Icon: Package },
  { label: 'Runners’ Stories', to: '/runners-stories', testId: 'explore-student-stories', Icon: Trophy },
  { label: 'Contact LGRA', to: '/#contact', testId: 'explore-contact', Icon: Phone },
];

/**
 * Shared handler used by both this desktop dropdown and the mobile drawer so
 * behaviour is identical everywhere. Exported so `MobileNavDrawer` can call
 * it too without duplicating the anchor-vs-route logic.
 */
export const navigateExplore = (target, { navigate, currentPathname, onClose }) => {
  if (onClose) onClose();
  if (target.startsWith('/#')) {
    const id = target.slice(2);
    if (currentPathname === '/') {
      // Same-page anchor — smooth scroll without triggering a router change.
      // The homepage lazy-mounts several axios-driven sections which grow the
      // document height AFTER the initial scroll fires (typical on mobile),
      // so we re-anchor on a schedule matching PublicLanding's hash effect.
      const doScroll = () => {
        if (id === 'top') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      };
      doScroll();
      [200, 600, 1200, 2000, 3200].forEach((ms) => setTimeout(doScroll, ms));
      window.history.replaceState(null, '', target);
      return;
    }
    // Cross-page: route to homepage with hash — `PublicLanding` will scroll.
    navigate(target);
    return;
  }
  // Non-anchor destinations (e.g. /runners-stories) — normal navigation.
  navigate(target);
};

export const EXPLORE_GROUPS = { START_HERE, TRAINING_OPTIONS, EXPLORE_MORE };

const MenuItemRow = ({ item, onSelect }) => {
  const Icon = item.Icon;
  return (
    <DropdownMenuItem
      onSelect={() => onSelect(item.to)}
      className="cursor-pointer gap-2.5 py-2 focus:bg-[#F5F3FF] focus:text-[#3F2774]"
      data-testid={item.testId}
    >
      <span className="w-6 h-6 rounded-md bg-[#F5F3FF] text-[#7C3AED] flex items-center justify-center shrink-0">
        <Icon size={13} strokeWidth={2.25} />
      </span>
      <span className="text-sm font-semibold text-slate-800 flex-1">{item.label}</span>
      {item.badge && (
        <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#7C3AED] text-white">
          {item.badge}
        </span>
      )}
    </DropdownMenuItem>
  );
};

export const ExploreLGRAMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { visible: aiVisible } = useAIFormCheckVisibility();
  const trainingOptions = TRAINING_OPTIONS.filter((item) => aiVisible || item.to !== '/ai-running-form-check');

  const onSelect = (target) => {
    navigateExplore(target, { navigate, currentPathname: location.pathname });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="hidden lg:inline-flex items-center gap-1.5 h-9 px-2.5 rounded-md border border-slate-200 bg-white text-slate-700 hover:text-[#3F2774] hover:border-[#D8C4FF] hover:bg-[#FBF8FF] text-xs font-black uppercase tracking-widest transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]"
          aria-label="Explore LGRA — open quick navigation"
          data-testid="explore-lgra-trigger"
        >
          <Compass size={13} className="text-[#7C3AED]" aria-hidden />
          <span>Explore</span>
          <ChevronDown size={11} className="text-slate-400" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={8}
        className="min-w-[280px] p-1.5 shadow-lg border-[#EDE9FE]"
        data-testid="explore-lgra-menu"
      >
        <DropdownMenuLabel className="text-[10px] font-black uppercase tracking-widest text-[#7C3AED] px-2 py-1.5">
          Start here
        </DropdownMenuLabel>
        {START_HERE.map((item) => (
          <MenuItemRow key={item.to} item={item} onSelect={onSelect} />
        ))}

        <DropdownMenuSeparator className="my-1.5 bg-[#F1EBFF]" />
        <DropdownMenuLabel className="text-[10px] font-black uppercase tracking-widest text-[#7C3AED] px-2 py-1.5">
          Training options
        </DropdownMenuLabel>
        {trainingOptions.map((item) => (
          <MenuItemRow key={item.to} item={item} onSelect={onSelect} />
        ))}

        <DropdownMenuSeparator className="my-1.5 bg-[#F1EBFF]" />
        <DropdownMenuLabel className="text-[10px] font-black uppercase tracking-widest text-[#7C3AED] px-2 py-1.5">
          Explore LGRA
        </DropdownMenuLabel>
        {EXPLORE_MORE.map((item) => (
          <MenuItemRow key={item.to} item={item} onSelect={onSelect} />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ExploreLGRAMenu;
